import { Controller } from "@hotwired/stimulus"

// SNS分析エージェントの設定画面「分析対象」(app/views/agent_detection_settings/_analysis_targets.html.erb)。
// - 方式のラジオ(すべて分析する / 一部を分析から外す)で、件数のまとめとタブ(チェック)を切り替える
// - SNS ごとのタブの切り替え(WAI-ARIA の Tabs パターン。左右の矢印キー・Home / End で移動して選ぶ)と、
//   モバイルでタブが横に見切れているときの右端のフェード
// - 「分析中」の件数の表示(count ターゲットの data-count-key と、チェックボックスの data-count-keys で対応づける)
// - 変更があるときだけ保存バーを出し、変更を破棄できるようにする。
//   変更の有無は、各入力の初期値(defaultChecked = 描画時の checked 属性)と今の値を比べて決める
// - 名前の絞り込み欄で Enter を押してもフォームを送信しない
// - 選んでいる SNS のタブを hidden の sns(snsField)に入れておき、保存後のリダイレクトで同じタブに戻す
//   (最初に選ぶタブはサーバーが描画で決める。readonly の子アカウントではこのコントローラ自体を付けない)
export default class extends Controller {
  static targets = ["mode", "allPanel", "customPanel", "tab", "panel", "item", "count", "bar", "changeCount", "tabScroller", "snsField"]

  connect() {
    this.update()
    this.revealSelectedTab()
  }

  // 保存後に戻ってきたとき・「一部を外す」に切り替えたとき、選ばれているタブ(最初はサーバーが描画で決める)が
  // 横スクロールの外にあれば見える位置まで寄せる(Threads は6番目で、モバイルでは見切れうる)。
  // scrollIntoView はページも縦にスクロールしてしまう(カードは画面の下の方にある)ので、タブ列の scrollLeft だけを動かす。
  // タブ列が隠れている(すべて分析する。幅 0)ときは何もしない
  revealSelectedTab() {
    if (!this.hasTabScrollerTarget) return
    const tab = this.tabTargets.find((element) => element.getAttribute("aria-selected") === "true")
    if (!tab) return

    const scroller = this.tabScrollerTarget
    const scrollerRect = scroller.getBoundingClientRect()
    const tabRect = tab.getBoundingClientRect()
    if (tabRect.right > scrollerRect.right) scroller.scrollLeft += tabRect.right - scrollerRect.right
    else if (tabRect.left < scrollerRect.left) scroller.scrollLeft -= scrollerRect.left - tabRect.left
    this.updateTabFade()
  }

  update() {
    const custom = this.isCustom()
    this.allPanelTargets.forEach((panel) => panel.classList.toggle("hidden", custom))
    this.customPanelTargets.forEach((panel) => panel.classList.toggle("hidden", !custom))
    this.countTargets.forEach((element) => {
      element.textContent = String(this.analyzedCount(element.dataset.countKey, custom))
    })

    const changes = this.changeCount()
    if (this.hasChangeCountTarget) this.changeCountTarget.textContent = String(changes)
    if (this.hasBarTarget) this.barTarget.classList.toggle("hidden", changes === 0)
    this.updateTabFade()
    // 「すべて分析する」で開いたときはタブ列が隠れていて connect では寄せられないので、「一部を外す」に切り替えたときに寄せる
    // (チェックを変えるたびには動かさない)
    if (custom && this.wasCustom === false) this.revealSelectedTab()
    this.wasCustom = custom
  }

  // タブの右に続きがあるあいだだけ data-fade="true" にする(CSS が右端をフェードさせる)。
  // 隠れているとき(すべて分析する)は幅が 0 なので false になり、「一部を外す」に切り替えた update で付け直す
  updateTabFade() {
    if (!this.hasTabScrollerTarget) return
    const scroller = this.tabScrollerTarget
    scroller.dataset.fade = String(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1)
  }

  // 絞り込み欄の Enter で、分析対象のフォームが暗黙に送信されるのを止める(日本語入力の変換の確定は止めない)
  preventEnterSubmit(event) {
    if (event.key !== "Enter" || event.isComposing) return
    event.preventDefault()
  }

  isCustom() {
    const selected = this.modeTargets.find((radio) => radio.checked)
    return selected ? selected.value === "custom" : false
  }

  // key に属する資産のうち分析するものの数。「すべて分析する」なら全件、「一部を外す」ならチェックしたものだけ
  analyzedCount(key, custom) {
    return this.itemTargets.filter((box) => {
      const keys = (box.dataset.countKeys || "").split(" ")
      return keys.includes(key) && (!custom || box.checked)
    }).length
  }

  // 未保存の変更の件数。方式が変わっていれば1件。チェックは「一部を外す」のときだけ保存されるので、そのときだけ1件ずつ数える
  changeCount() {
    const modeChanged = this.modeTargets.some((radio) => radio.checked !== radio.defaultChecked) ? 1 : 0
    if (!this.isCustom()) return modeChanged

    return modeChanged + this.itemTargets.filter((box) => box.checked !== box.defaultChecked).length
  }

  discard() {
    this.modeTargets.forEach((radio) => {
      radio.checked = radio.defaultChecked
    })
    this.itemTargets.forEach((box) => {
      box.checked = box.defaultChecked
    })
    this.refreshCheckAll()
    this.update()
  }

  // 区画の見出しのチェック(check-all の3状態)を描き直す。区画ごとに先頭のチェックボックスで change を起こす
  refreshCheckAll() {
    const sections = new Set()
    this.itemTargets.forEach((box) => {
      const section = box.closest('[data-controller~="check-all"]')
      if (!section || sections.has(section)) return

      sections.add(section)
      box.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  selectTab(event) {
    this.showTab(event.params.sns)
  }

  // 左右の矢印キーで前後のタブ(端では反対側へ回る)、Home / End で最初・最後のタブへ移り、そのタブを選ぶ
  tabKeydown(event) {
    const tabs = this.tabTargets
    const index = tabs.indexOf(event.currentTarget)
    if (index < 0) return

    const next = {
      ArrowRight: (index + 1) % tabs.length,
      ArrowLeft: (index - 1 + tabs.length) % tabs.length,
      Home: 0,
      End: tabs.length - 1,
    }[event.key]
    if (next === undefined) return

    event.preventDefault()
    this.showTab(tabs[next].dataset.sns, { focus: true })
  }

  // sns のタブだけを選択状態にしてパネルを出す。Tab キーで止まるのは選ばれているタブだけ(tabindex)。
  // 選んだタブが横に見切れていれば見える位置までスクロールする。保存後に同じタブへ戻すため、hidden の sns も書き換える
  showTab(sns, { focus = false } = {}) {
    if (this.hasSnsFieldTarget) this.snsFieldTarget.value = sns
    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.sns === sns
      tab.classList.toggle("tab-active", active)
      tab.classList.toggle("text-primary", active)
      tab.setAttribute("aria-selected", String(active))
      tab.setAttribute("tabindex", active ? "0" : "-1")
      if (!active) return

      if (focus) tab.focus()
      tab.scrollIntoView({ inline: "nearest", block: "nearest" })
    })
    this.panelTargets.forEach((panel) => panel.classList.toggle("hidden", panel.dataset.sns !== sns))
    this.updateTabFade()
  }
}
